import React, { useEffect, useMemo, useState } from "react";
import styles from "./TemplatesPage.module.css";
import CreateTemplateModal from "../../components/CreateTemplateModal/CreateTemplateModal";
import { FlaticonEditIcon, FlaticonRemoveIcon } from "../../assets/icons";
import { useLanguage } from "../../contexts/language/LanguageContext";
import { buildApiUrl } from "../../config/env";
const PAGE_SIZE = 5;
const TemplatesPage = () => {
  const { t } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch(buildApiUrl("packages/"));
      const data = await res.json();
      if (Array.isArray(data)) setTemplates(data);
    } catch (err) {
      console.error("Fetch templates failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    setSearch("");
    setPage(1);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter((t) =>
      `${t.name} ${t.sample}`.toLowerCase().includes(term),
    );
  }, [templates, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const sliceStart = (currentPage - 1) * PAGE_SIZE;
  const paginated = filtered.slice(sliceStart, sliceStart + PAGE_SIZE);

  const handleDelete = async (tpl) => {
    const confirmDelete = window.confirm(
      t("templates_confirm_delete", { name: tpl.name }),
    );
    if (!confirmDelete) return;
    setLoading(true);
    try {
      const res = await fetch(buildApiUrl(`packages/${tpl._id}`), {
        method: "DELETE",
      });
      if (res.ok) {
        setTemplates((prev) => prev.filter((t) => t._id !== tpl._id));
      }
    } catch (err) {
      console.error("Delete template failed", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaved = (tpl) => {
    if (!tpl?._id) return;
    setTemplates((prev) => {
      const exists = prev.some((t) => t._id === tpl._id);
      if (exists) return prev.map((t) => (t._id === tpl._id ? tpl : t));
      return [tpl, ...prev];
    });
    setEditorOpen(false);
    setEditingTemplate(null);
  };

  const openCreate = () => {
    setEditingTemplate(null);
    setEditorOpen(true);
  };

  const openEdit = (tpl) => {
    setEditingTemplate(tpl);
    setEditorOpen(true);
  };

  //  if (!open) return null;

  return (
    <>
      <div className={styles.pageContainer}>
        <div className={styles.box}>
          {/* Header */}
          <div className={styles.header}>
            <div>
              <h3>{t("templates_title")}</h3>
            </div>
          </div>

          {/* Controls */}
          <div className={styles.controls}>
            <input
              className={styles.search}
              placeholder={t("templates_search_placeholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className={styles.actions}>
              <button
                className={styles.btn}
                onClick={fetchTemplates}
                disabled={loading}
              >
                {t("templates_btn_reload")}
              </button>
              <button
                // Ghép 2 class: btn (cơ bản) + primary (màu xanh)
                className={`${styles.btn} ${styles.primary}`}
                onClick={openCreate}
              >
                {t("templates_btn_create")}
              </button>
            </div>
          </div>

          {/* Table List */}
          <div className={styles.table}>
            {/* Header Row */}
            <div className={`${styles.row} ${styles.head}`}>
              <div className={styles.col}>{t("templates_col_name")}n</div>
              <div className={styles.col}>{t("templates_col_sample_name")}</div>
              <div className={styles.col_center}>
                {t("templates_col_index_count")}
              </div>
              <div className={`${styles.col} ${styles.colActions}`}>
                {t("templates_col_actions")}
              </div>
            </div>

            {/* Data Rows */}
            {paginated.map((tpl) => (
              <div className={styles.row} key={tpl._id}>
                <div className={styles.col}>
                  <strong>{tpl.name}</strong>
                </div>
                <div className={styles.col} title={tpl.sample}>
                  {tpl.sample}
                </div>
                <div className={styles.col_center}>
                  {tpl.indexes?.length || 0}
                </div>
                <div className={`${styles.col} ${styles.colActions}`}>
                  <button className={styles.link} onClick={() => openEdit(tpl)}>
                    <img src={FlaticonEditIcon} />
                  </button>
                  <button
                    className={`${styles.link} ${styles.linkDanger}`}
                    onClick={() => handleDelete(tpl)}
                  >
                    <img src={FlaticonRemoveIcon} />
                  </button>
                </div>
              </div>
            ))}

            {paginated.length === 0 && (
              <div className={styles.empty}>{t("templates_empty_list")}</div>
            )}
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <div className={styles.pagination}>
              <button
                className={styles.btn}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                {t("templates_page_prev")}
              </button>
              <span className={styles.pageInfo}>
                {t("templates_page_info", {
                  current: currentPage,
                  total: totalPages || 1,
                })}
              </span>
              <button
                className={styles.btn}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                {t("templates_page_next")}
              </button>
            </div>
            {loading && (
              <div className={styles.loading}>{t("templates_loading")}</div>
            )}
          </div>
        </div>
      </div>

      <CreateTemplateModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={handleSaved}
        initialTemplate={editingTemplate}
        mode={editingTemplate ? "edit" : "create"}
      />
    </>
  );
};

export default TemplatesPage;
